import { useEffect, useMemo, useRef } from 'react';
import { useStore } from '@tanstack/react-form';
import { useLazyGetCurrencyRateQuery } from '../grid/api/gridApi';
import { extractErrorMessage } from '../../utils/errorUtils';
import type { DetailFormJsMethodProps } from './registry';
import { parseCurrencyRateJsMethodOptions, resolveObserverFieldName } from './currencyRateOptions';
import { useJsMethodLoading } from './jsMethodLoadingContext';

const formatLocalDate = (value: Date): string => {
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${value.getFullYear()}-${month}-${day}`;
};

const toDateOnly = (value: unknown): string | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : formatLocalDate(value);
  }

  if (typeof value !== 'string' || value.trim().length === 0) {
    return null;
  }

  const trimmed = value.trim();
  return /^\d{4}-\d{2}-\d{2}/.test(trimmed) ? trimmed.slice(0, 10) : null;
};

const todayDateOnly = (): string => {
  return formatLocalDate(new Date());
};

export const CurrencyRateJsMethodBinding = ({
  field,
  fields,
  form,
  onNotify,
}: DetailFormJsMethodProps) => {
  const { setLoading } = useJsMethodLoading();
  const loadingKey = `${field.key ?? field.dataField ?? field.field}:${field.nameJsMethod}`;
  const options = useMemo(
    () => parseCurrencyRateJsMethodOptions(field.jsMethodOptions),
    [field.jsMethodOptions]
  );
  const observerFieldName = useMemo(
    () => (options ? resolveObserverFieldName(fields, options.observerIdElement) : null),
    [fields, options]
  );
  const observedValue = useStore(form.store, (state) => {
    const values = (state as { values?: Record<string, unknown> }).values;
    return observerFieldName ? values?.[observerFieldName] : undefined;
  });
  const [getCurrencyRate] = useLazyGetCurrencyRateQuery();
  const previousValue = useRef<string | null | undefined>(undefined);
  const requestSequence = useRef(0);

  useEffect(() => {
    previousValue.current = undefined;
  }, [observerFieldName]);

  useEffect(() => {
    if (!options || !observerFieldName) {
      return;
    }

    const selectedDate = toDateOnly(observedValue);
    if (previousValue.current === undefined) {
      previousValue.current = selectedDate;
      return;
    }

    if (previousValue.current === selectedDate) {
      return;
    }

    previousValue.current = selectedDate;
    const requestId = ++requestSequence.current;
    if (!selectedDate) {
      setLoading(loadingKey, false);
      return;
    }

    const date = selectedDate > todayDateOnly() ? todayDateOnly() : selectedDate;
    let isActive = true;
    const timeoutId = window.setTimeout(async () => {
      setLoading(loadingKey, true);
      try {
        const rate = await getCurrencyRate({
          date,
          nameApi: options.nameApi,
          fromCurrency: options.fromCurrency,
          toCurrency: options.toCurrency,
        }).unwrap();

        if (!isActive || requestId !== requestSequence.current) {
          return;
        }

        const targetFieldName = field.dataField;
        if (!targetFieldName) {
          return;
        }

        form.setFieldValue(targetFieldName, String(rate));
        onNotify({ severity: 'success', message: 'Exchange rate updated.' });
      } catch (error) {
        if (isActive && requestId === requestSequence.current) {
          onNotify({
            severity: 'error',
            message: extractErrorMessage(error, 'Failed to update exchange rate.'),
          });
        }
      } finally {
        if (isActive && requestId === requestSequence.current) {
          setLoading(loadingKey, false);
        }
      }
    }, 1000);

    return () => {
      window.clearTimeout(timeoutId);
      isActive = false;
      setLoading(loadingKey, false);
    };
  }, [
    field.dataField,
    form,
    getCurrencyRate,
    loadingKey,
    observedValue,
    observerFieldName,
    onNotify,
    options,
    setLoading,
  ]);

  return null;
};
